/**
 * Equipment Tracker - API Service
 * @author Omid Zaeri (zaerio)
 *
 * All API calls pass the Midway JWT as a Bearer token in the
 * Authorization header. The backend validates the JWT and extracts
 * the caller alias from it.
 */

import config from '../config';

// Module-level token store — set by AuthContext after Midway login
let _midwayToken = null;

export function setMidwayToken(token) {
  _midwayToken = token;
}

async function request(path, options = {}, tokenOverride) {
  const url = `${config.apiUrl}${path}`;
  const token = tokenOverride || _midwayToken;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    ...options,
    headers,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let message = res.statusText || 'Request failed';
    try { message = JSON.parse(text).message || message; } catch { /* not JSON */ }
    throw new Error(message);
  }
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error('Your status could not be verified as a manager. If you believe you\'ve received this message in error, please contact zaerio@amazon.com');
  }
}

// Users
export const getUsers = (manager, viewAs) => {
  const params = new URLSearchParams();
  if (manager) params.set('manager', manager);
  if (viewAs) params.set('viewAs', viewAs);
  const qs = params.toString();
  return request(`/users${qs ? `?${qs}` : ''}`);
};
export const getManagers = () => request('/users?listManagers=true');
export const getUser = (alias, token) => request(`/users/${alias}`, {}, token);
export const createUser = (data) => request('/users', { method: 'POST', body: JSON.stringify(data) });
export const updateUser = (alias, data) => request(`/users/${alias}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteUser = async (alias, confirm = false) => {
  const url = `${config.apiUrl}/users/${alias}${confirm ? '?confirm=true' : ''}`;
  const token = _midwayToken;
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { method: 'DELETE', headers });
  const body = await res.json().catch(() => ({ message: res.statusText }));
  if (res.status === 409) return { needsConfirmation: true, ...body };
  if (!res.ok) throw new Error(body.message || 'Request failed');
  return body;
};

// Equipment
export const getEquipment = (viewAs) => request(`/equipment${viewAs ? `?viewAs=${viewAs}` : ''}`);
export const getEquipmentById = (id) => request(`/equipment/${id}`);
export const createEquipment = (data) => request('/equipment', { method: 'POST', body: JSON.stringify(data) });
export const updateEquipment = (id, data) => request(`/equipment/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteEquipment = (id) => request(`/equipment/${id}`, { method: 'DELETE' });
export const acceptPolicy = (id) => request(`/equipment/${id}/accept-policy`, { method: 'POST' });

// Status Reports
export const getStatusReports = (equipmentId) => request(`/status-reports${equipmentId ? `?equipmentId=${equipmentId}` : ''}`);
export const submitStatusReport = (data) => request('/status-reports', { method: 'POST', body: JSON.stringify(data) });

// Admin
export const grantAdmin = (alias) => request('/admin/grant-admin', { method: 'POST', body: JSON.stringify({ alias }) });
export const revokeAdmin = (alias) => request('/admin/revoke-admin', { method: 'POST', body: JSON.stringify({ alias }) });

// Lookup
export const lookupUser = (alias) => request(`/lookup/${alias}`);
export const getTeamEquipment = (viewAs) => request(`/users/team${viewAs ? `?viewAs=${viewAs}` : ''}`);

// Self-registration
export const checkSelfRegister = (token, alias) => request(`/self-register${alias ? `?alias=${alias}` : ''}`, {}, token);
export const selfRegister = (token, alias) => {
  return request('/self-register', { method: 'POST', body: JSON.stringify({ alias }) }, token);
};

// Trigger a pending lookup so the Cloud Desktop worker resolves it
export const triggerLookup = (token, alias) => {
  return request(`/self-register?alias=${alias}`, {}, token || undefined).catch(() => null);
};

// Browser-side phonetool fetch (user has Midway cookies for phonetool.amazon.com)
async function fetchPhoneTool(alias) {
  try {
    const res = await fetch(`https://phonetool.amazon.com/users/${alias}.json`, {
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.name ? data : null;
  } catch {
    return null;
  }
}

// Direct phonetool lookup via CloudFront proxy
// The /phonetool/* behavior forwards to phonetool.amazon.com with the full path.
// phonetool.amazon.com may redirect /phonetool/users/X.json → /users/X.json
export async function phonetoolLookup(alias) {
  // Try the backend lookup endpoint first (uses cache + live fetch)
  try {
    const data = await request(`/lookup/${alias}`);
    if (data && data.name && !data.lookupFailed) {
      return { name: data.name, email: data.email, job_title: data.title, manager: { login: data.manager } };
    }
  } catch { /* fall through */ }
  // Try browser-side fetch via CloudFront phonetool proxy
  try {
    const res = await fetch(`/phonetool/users/${alias}.json`, { credentials: 'include' });
    if (res.ok) {
      const text = await res.text();
      try { return JSON.parse(text); } catch { return null; }
    }
  } catch { /* ignore */ }
  return null;
}
